// Excel + PDF + Print export — Türkçe karakter desteği garanti.
// XLSX: UTF-8 native. PDF: html2canvas + jsPDF (tarayıcı font engine → ç,ğ,ı,ö,ş,ü mükemmel).
//
// PDF algoritması — çok sayfa için "row chunking":
//   • Satırlar sabit boyutlu sayfa parçalarına bölünür (SAT_PER_SAYFA)
//   • Her sayfa AYNI DOM template'i ile ayrı bir container olarak render edilir
//     (başlık + tablo + o sayfaya ait satırlar).
//   • html2canvas her container'ı ayrı yakalar → jsPDF'e tek görsel olarak eklenir.
//   • Sonuç: HİÇBİR koordinat / dilimleme hesabı yok → başlık ile 1. satır arasında boşluk YOK.
//
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// -------- helpers: Türkçe format (nokta ile ayrılmış tarih ve para) --------
const MONEY_HEADER_RE = /tutar|limit|toplam|ekstre|bakiye|borç|alacak|net|kdv|birim|fiyat|price|amount/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function formatTRDate(iso) {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function formatTRMoney(v) {
  const num = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  if (!Number.isFinite(num)) return String(v);
  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function isMoneyColumn(col) {
  const header = String(col.header || "");
  const key = String(col.key || "");
  return MONEY_HEADER_RE.test(header) || MONEY_HEADER_RE.test(key);
}

function cellValue(row, col) {
  const raw = col.accessor ? col.accessor(row) : row[col.key];
  if (raw === null || raw === undefined || raw === "") return "";
  if (col.accessor) return typeof raw === "number" ? raw : String(raw);
  if (typeof raw === "string" && ISO_DATE_RE.test(raw)) return formatTRDate(raw);
  if (isMoneyColumn(col)) {
    const num = typeof raw === "number" ? raw : parseFloat(String(raw).replace(",", "."));
    if (Number.isFinite(num)) return formatTRMoney(num);
  }
  if (typeof raw === "number") return raw;
  return String(raw);
}

// ================= EXCEL =================
export function exportExcel(filename, rows, columns) {
  const header = columns.map((c) => c.header);
  const data = rows.map((r) => columns.map((c) => cellValue(r, c)));
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);

  const widths = columns.map((c) => {
    const headerLen = String(c.header || "").length;
    const maxCellLen = Math.max(
      headerLen,
      ...data.map(row => String(row[columns.indexOf(c)] ?? "").length),
    );
    return { wch: Math.min(Math.max(maxCellLen + 2, 12), 40) };
  });
  ws["!cols"] = widths;

  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F2937" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    };
  }

  ws["!autofilter"] = { ref: ws["!ref"] };
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  const sheetName = String(filename || "Veri").slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`, { bookType: "xlsx", cellStyles: true });
}

// ================= PDF (row-chunk render) =================
// Options:
//   - summary: [{label, value, color?, subtitle?}]  → PDF üstünde özet blok gösterilir
//   - sections: [{label, rows}]  → sağlanırsa `rows` argümanı yok sayılır; her section
//               kendi başlığı ile ayrı bir sayfa bloğu olarak render edilir.
//   - returnBlob: true → PDF'i indirmez, Blob döndürür (Bulk ZIP export için)
//   - filenameHint: dosya adı (returnBlob true ise başlık farklı olabilir)
//   - rowsPerPage: default 22 (A4 yatay için güvenli)
export async function exportPDF(title, rows, columns, options = {}) {
  const { summary, sections, returnBlob = false, filenameHint, rowsPerPage: rppOpt } = options;

  // Sayfa başına satır sayısı: kolon sayısına göre auto-tune
  const rppAuto = columns.length <= 6 ? 22
                : columns.length <= 9 ? 18
                : 14;
  const ROWS_PER_PAGE = Math.max(5, Math.min(40, rppOpt || rppAuto));

  // Sections varsa → gruplandırılmış render. Yoksa → tek grup (label boş).
  const groups = sections && sections.length > 0
    ? sections
    : [{ label: null, rows: rows || [] }];

  // Toplam sayfa sayısı hesabı (grup içi paginasyon)
  const groupPageCounts = groups.map(g =>
    Math.max(1, Math.ceil((g.rows || []).length / ROWS_PER_PAGE))
  );
  const totalPages = groupPageCounts.reduce((a, b) => a + b, 0);
  const totalRows = groups.reduce((sum, g) => sum + (g.rows || []).length, 0);

  const now = new Date().toLocaleString("tr-TR");

  // jsPDF A4 yatay
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;
  const CONTAINER_W_PX = 1100;

  const buildPageHTML = ({ pageRows, globalPageIdx, sectionLabel, isFirstOfSection, isFirstPageOverall }) => {
    const th = columns.map((c) => {
      const align = c.align === "right" ? "right" : "left";
      return `<th style="background:#1f2937;color:#fff;padding:8px 10px;font-weight:600;font-size:11px;text-align:${align};border:1px solid #1f2937;">${escapeHtml(c.header)}</th>`;
    }).join("");

    const tbody = pageRows.map((r, i) => {
      const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
      const tds = columns.map((c) => {
        const val = cellValue(r, c);
        const align = c.align === "right" ? "right" : "left";
        return `<td style="padding:6px 10px;font-size:10px;text-align:${align};border:1px solid #e5e7eb;background:${bg};vertical-align:top;">${escapeHtml(val ?? "")}</td>`;
      }).join("");
      return `<tr>${tds}</tr>`;
    }).join("");

    // Özet bloğu: SADECE ilk sayfada göster
    const summaryHTML = (isFirstPageOverall && summary && summary.length > 0)
      ? `<div style="display:flex;flex-wrap:wrap;gap:12px;margin:0 0 12px;">
           ${summary.map(s => `
             <div style="flex:1 1 180px;min-width:180px;background:#f8fafc;border:1px solid #e5e7eb;padding:10px 12px;border-radius:6px;">
               <div style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">${escapeHtml(s.label)}</div>
               <div style="font-size:14px;font-weight:700;color:${s.color || '#111'};margin-top:2px;font-variant-numeric:tabular-nums;">${escapeHtml(s.value)}</div>
               ${s.subtitle ? `<div style="font-size:8px;color:#9ca3af;margin-top:2px;">${escapeHtml(s.subtitle)}</div>` : ""}
             </div>
           `).join("")}
         </div>`
      : "";

    // Section başlığı: bölüm ilk sayfasında (grup ilk sayfası)
    const sectionHeaderHTML = (sectionLabel && isFirstOfSection)
      ? `<div style="margin:6px 0 10px;padding:8px 12px;background:#eef2ff;border-left:4px solid #4f46e5;border-radius:4px;">
           <div style="font-size:13px;font-weight:700;color:#3730a3;">${escapeHtml(sectionLabel)}</div>
         </div>`
      : (sectionLabel
          ? `<div style="margin:6px 0 8px;font-size:11px;color:#6b7280;font-style:italic;">${escapeHtml(sectionLabel)} (devam)</div>`
          : "");

    return `
      <div style="border-bottom:2px solid #1f2937;padding-bottom:10px;margin-bottom:12px;">
        <div style="display:flex;justify-content:space-between;align-items:baseline;">
          <h1 style="font-size:18px;margin:0;font-weight:700;">${escapeHtml(title)}</h1>
          <div style="font-size:10px;color:#666;">Sayfa ${globalPageIdx + 1} / ${totalPages}</div>
        </div>
        <div style="font-size:10px;color:#666;margin-top:3px;">Rapor Tarihi: ${now} · Toplam Kayıt: ${totalRows}</div>
      </div>
      ${summaryHTML}
      ${sectionHeaderHTML}
      <table style="border-collapse:collapse;width:100%;font-family:inherit;table-layout:auto;">
        <thead><tr>${th}</tr></thead>
        <tbody>${tbody || `<tr><td colspan="${columns.length}" style="padding:20px;text-align:center;font-size:11px;color:#666;">Kayıt bulunamadı</td></tr>`}</tbody>
      </table>
    `;
  };

  // Off-screen render helper — html2canvas için container yaratır
  const renderPage = async (pageCtx) => {
    const container = document.createElement("div");
    container.style.cssText = [
      "position:fixed",
      "top:-99999px",
      "left:0",
      `width:${CONTAINER_W_PX}px`,
      "background:#ffffff",
      "font-family:'IBM Plex Sans',-apple-system,Segoe UI,Tahoma,Verdana,sans-serif",
      "color:#111",
      "padding:20px",
      "box-sizing:border-box",
    ].join(";");
    container.innerHTML = buildPageHTML(pageCtx);
    document.body.appendChild(container);

    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: CONTAINER_W_PX + 40,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);

      const imgWmm = usableW;
      const imgHmm = (canvas.height * usableW) / canvas.width;

      let finalW = imgWmm, finalH = imgHmm;
      if (imgHmm > usableH) {
        finalH = usableH;
        finalW = (canvas.width * usableH) / canvas.height;
      }

      const xOffset = margin + (usableW - finalW) / 2;
      const yOffset = margin;

      if (pageCtx.globalPageIdx > 0) pdf.addPage();
      pdf.addImage(imgData, "JPEG", xOffset, yOffset, finalW, finalH);
    } finally {
      if (container.parentNode) document.body.removeChild(container);
    }
  };

  // Render each group's pages sequentially
  let globalPageIdx = 0;
  for (let gi = 0; gi < groups.length; gi++) {
    const g = groups[gi];
    const gRows = g.rows || [];
    const gPages = groupPageCounts[gi];
    for (let p = 0; p < gPages; p++) {
      const start = p * ROWS_PER_PAGE;
      const end = start + ROWS_PER_PAGE;
      await renderPage({
        pageRows: gRows.slice(start, end),
        globalPageIdx,
        sectionLabel: g.label,
        isFirstOfSection: p === 0,
        isFirstPageOverall: globalPageIdx === 0,
      });
      globalPageIdx++;
    }
  }

  if (returnBlob) {
    return pdf.output("blob");
  }

  const safeName = (filenameHint || title).replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ_. -]/g, "_").slice(0, 80);
  pdf.save(`${safeName}.pdf`);
  return null;
}

function escapeHtml(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// ================= YAZDIR / PRINT =================
export function printData(title, rows, columns) {
  const w = window.open("", "_blank");
  if (!w) return;
  const th = columns.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");
  const rowsHtml = rows.map((r) => `<tr>${
    columns.map((c) => {
      const align = c.align === "right" ? "text-align:right;" : "";
      return `<td style="${align}">${escapeHtml(cellValue(r, c) ?? "")}</td>`;
    }).join("")
  }</tr>`).join("");
  w.document.write(`<!DOCTYPE html>
    <html lang="tr"><head><meta charset="UTF-8"><title>${escapeHtml(title)}</title>
    <style>
      body{font-family:'IBM Plex Sans',Segoe UI,Tahoma,sans-serif;padding:24px;color:#111}
      h1{font-size:20px;margin:0 0 6px}
      .meta{font-size:11px;color:#666;margin-bottom:16px}
      table{border-collapse:collapse;width:100%;font-size:11px}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
      th{background:#18181b;color:#fff;font-weight:600}
      tr:nth-child(even) td{background:#fafafa}
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">${new Date().toLocaleString("tr-TR")} · Kayıt: ${rows.length}</div>
    <table><thead><tr>${th}</tr></thead>
    <tbody>${rowsHtml}</tbody></table>
    <script>window.print();</script>
    </body></html>`);
  w.document.close();
}
